import * as React from 'react';
import { FilterProviderBase, FilterEditProps, isGenericEmptyValue, QueryStringTarget } from '.';
import MultipleTextFilterEdit from '../editors/MultipleTextFilterEdit';
import MultipleDropDownFilterEdit from '../editors/MultipleDropDownFilterEdit';
import { Filter, FilterString, FilterNotSet, FilterOptions } from '..';
import { MultipleValueDelimiter } from './Constants';

export interface StringArrayFilterEditProps extends FilterEditProps<string[]> {}
export interface NumberArrayFieldEditProps extends FilterEditProps<number[]> {}

/**
 * Default multiple value field provider. Treats the query as a delimeter separated list of strings.
 *
 * @export
 * @class MultipleValueFieldProvider
 * @implements {FieldProviderBase<any[]>}
 */
export class MultipleValueFilterProvider implements FilterProviderBase<any[]> {
	public getComponent = (): React.ComponentType => MultipleTextFilterEdit;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter: Filter) => ({});
	public getFriendlyValueForDisplay = (value: any[], filterOptions?: FilterOptions): React.ReactNode => {
		if (filterOptions != null && filterOptions.dropdownValues != null) {
			const friendlyNames = filterOptions.dropdownValues.reduce((acc, obj) => {
				if (!acc[obj.value]) {
					acc[obj.value] = obj.text;
				}
				return acc;
			}, {});
			return value.length === 0 ? (
				<FilterNotSet />
			) : (
				value.reduce((acc, obj, ind, arr) => {
					if (friendlyNames[obj] !== undefined) {
						acc += friendlyNames[obj];
					} else {
						acc += obj;
					}

					if (ind < arr.length - 1) {
						acc += ', ';
					}

					return acc;
				}, '')
			);
		} else {
			return value.length === 0 ? <FilterNotSet /> : value.map((f) => FilterString(f, filterOptions)).join(', ');
		}
	};
	public getValueFromQueryString = (query: string): any[] | null => query.split(MultipleValueDelimiter);
	// eslint-disable-next-line no-unused-vars
	public getQueryStringFromValue = (value: any[], filter: Filter, target: QueryStringTarget): string =>
		!Array.isArray(value) ? '' : value.join(MultipleValueDelimiter);

	public isEmptyValue = (value: any[]): boolean => {
		if (isGenericEmptyValue(value)) {
			return true;
		}

		if (
			Array.isArray(value) &&
			(value.length === 0 || value.filter((val) => !isGenericEmptyValue(val)).length === 0)
		) {
			return true;
		}

		return false;
	};
}

/**
 * Provider for multiple values selected via a dropdown
 *
 * @export
 * @class MultipleDropdownFilterProvider
 * @extends {MultipleValueFilterProvider}
 */
export class MultipleDropdownFilterProvider extends MultipleValueFilterProvider {
	public getComponent = (): React.ComponentType => MultipleDropDownFilterEdit;
	public getComponentProps = (filter: Filter) =>
		(filter.options && { dropdownValues: filter.options.dropdownValues }) || {};
}
